import { useQuery } from '@tanstack/react-query'
import { api } from '../lib/api'

/**
 * A two-way choice used when creating an API key or an SMTP credential:
 *
 *   Overall  — works for every verified domain on the account.
 *   Specific — locked to the domains you tick, so a leaked credential cannot send from
 *              your other domains.
 *
 * `value` is the list of allowed domains; an empty list means "overall" (no restriction),
 * which is exactly how the API interprets an absent allowed_domains.
 */
export function DomainScope({
  value, onChange,
}: { value: string[]; onChange: (domains: string[]) => void }) {
  const { data } = useQuery({ queryKey: ['domains'], queryFn: api.domains.list })
  const domains = (data?.data ?? []).filter(d => d.can_send)

  const specific = value.length > 0

  function toggle(name: string) {
    onChange(value.includes(name) ? value.filter(d => d !== name) : [...value, name])
  }

  return (
    <div className="field">
      <label>Domain access</label>
      <div className="scope-choice">
        <button type="button" className={!specific ? 'active' : ''} onClick={() => onChange([])}>
          Overall
          <span className="scope-sub">all verified domains</span>
        </button>
        <button type="button" className={specific ? 'active' : ''}
                onClick={() => onChange(domains[0] ? [domains[0].name] : [])}>
          Specific
          <span className="scope-sub">chosen domains only</span>
        </button>
      </div>

      {specific && (
        <div className="scope-list">
          {domains.length === 0 && (
            <div className="small muted">No verified domains yet — verify one first.</div>
          )}
          {domains.map(d => (
            <label key={d.id} className="scope-item">
              <input type="checkbox" checked={value.includes(d.name)} onChange={() => toggle(d.name)} />
              <span>{d.name}</span>
            </label>
          ))}
        </div>
      )}

      <div className="hint">
        {specific
          ? 'This credential can only send from the ticked domains.'
          : 'This credential can send from every verified domain on the account.'}
      </div>
    </div>
  )
}
